import React, { useState, useEffect, MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Box, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { useAuth, useListing } from 'hooks';
import { hideModal } from 'state/actions';
import { MODAL, SERVICEMODALSTEP, SERVICE_OFFER_NAME } from 'config/constants';
import { HvModal, HvButton } from 'components';
import { remove } from 'lodash';
import { Picture, VirtualServiceOrder } from 'models';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const useStyles = makeStyles({
  cancelButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  slider: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rightArrow: {
    position: 'absolute',
    top: '50%',
    right: '32px',
    fontSize: 5,
    fill: 'white',
    zIndex: 10,
    cursor: 'pointer',
    userSelect: 'none',
  },
  leftArrow: {
    position: 'absolute',
    top: '50%',
    left: '32px',
    fontSize: 10,
    fill: 'blue',
    zIndex: 10,
    cursor: 'pointer',
    userSelect: 'none',
  },
  content: {
    flexDirection: 'column',
    display: 'flex',
  },
  thumbnails: {
    maxHeight: 500,
    overflowY: 'auto',
    position: 'relative',
    backgroundColor: '#f0f0f0',
    padding: 10,
    borderRadius: 8,
    '@media (max-width: 500px)': {
      maxHeight: 300,
    },
  },
  thumbnailContent: {
    position: 'relative',
  },
  image: {
    marginRight: 15,
    marginBottom: 10,
    width: '100%',
  },
  option: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 8,
    marginBottom: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  code: {
    width: '100%',
    marginRight: 10,
  },
  thumbnail: {
    width: '100%',
    height: 'auto',
  },
  checkbox: {
    position: 'absolute',
    left: 0,
    top: 0,
    cursor: 'pointer',
    '&::after': {
      position: 'absolute',
    },
  },
  selectedImageContent: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 10,
    alignItems: 'center',
  },
  selectedImage: {
    width: 'auto',
    maxHeight: 300,
    '@media (max-width: 767px)': {
      width: '100%',
    },
  },
  selectedThumbnailContent: {
    flexGrow: 1,
  },
});
interface ParamTypes {
  token: string;
}
interface Props {
  setActiveStep: (_: number) => void;
  virtualServiceOrder: VirtualServiceOrder;
  selectedService: any;
  setOrder: (virtualServiceOrder: VirtualServiceOrder) => void;
  selectedImages: any;
  setSelectedImages: (_: any) => void;
}
function VirtualServiceOption({
  setActiveStep,
  virtualServiceOrder,
  selectedService,
  setOrder,
  selectedImages,
  setSelectedImages,
}: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();

  const { user } = useAuth();
  const path = window.location.pathname;
  const arr = path.split('/');
  const token = arr[arr.length - 1];
  const { listing } = useListing(token);
  const photos = [];
  const [items, setItems] = useState([]);
  const defaultDelegated = false;
  const [isDelegated, setIsDelegated] = useState(defaultDelegated);
  const [code, setCurrentCode] = useState('');
  const [currentImageUrl, setCurrentImageUrl] = useState(listing.pictures[0].thumbnail_url);
  const [currentImageId, setCurrentImageId] = useState(0);
  listing.pictures.forEach((pic: any) => {
    photos.push({
      original: pic.original_url,
      thumbnail: pic.thumbnail_url,
    });
  });
  if (virtualServiceOrder && virtualServiceOrder.virtualServiceOrderItems.length) {
    virtualServiceOrder.virtualServiceOrderItems.map((item) => {
      return !selectedImages.includes(item.picture_id) && selectedImages.push(item.picture_id);
    });
  }
  useEffect(() => {
    // eslint-disable-next-line no-nested-ternary
    const items = virtualServiceOrder
      ? virtualServiceOrder.virtualServiceOrderItems
        ? virtualServiceOrder.virtualServiceOrderItems
        : []
      : [];
    setItems(items);
  }, []);
  const onClose = () => {
    dispatch(hideModal(MODAL.VIRTUAL_STAGING));
  };
  const onBack = () => {
    setActiveStep(SERVICEMODALSTEP.EXAMPLE);
  };

  const onNext = () => {
    if (selectedService.display_name === SERVICE_OFFER_NAME.VIRTUAL_BLUE_HOUR) {
      setActiveStep(SERVICEMODALSTEP.BLUEHOUR);
    } else {
      setActiveStep(SERVICEMODALSTEP.REVIEW);
    }
  };

  const onClickImage = (pic: Picture) => {
    setCurrentImageId(pic.id);
    setCurrentImageUrl(pic.thumbnail_url);
    const foundCurrentItem = items.find((item) => {
      return item.picture_id === pic.id;
    });
    if (foundCurrentItem) {
      setIsDelegated(foundCurrentItem.isDelegated);
      if (foundCurrentItem.code) {
        setCurrentCode(foundCurrentItem.code);
      } else {
        setCurrentCode('');
      }
    } else {
      setCurrentCode('');
      setIsDelegated(defaultDelegated);
      items.push({
        id: null,
        picture_id: pic.id,
        picture_thumbnail_url: pic.thumbnail_url,
        picture_url: pic.original_url,
        isDelegated: defaultDelegated,
        code: '',
        note: '',
      });
    }
    setOrder({
      id: null,
      listing_id: listing.id,
      virtualServiceOrderItems: items,
      user_id: user.id,
      created_at: new Date(),
      shooting_id: listing.validated_shooting_id,
      service_offer_id: selectedService.id,
    });
  };
  const unselect = (picture: Picture) => {
    const newItems = remove(items, (item) => {
      return item.picture_id !== picture.id;
    });
    const newSelectedImages = remove(selectedImages, (image) => {
      return image !== picture.id;
    });
    if (!newItems.length) {
      setOrder(null);
    } else {
      const previousSelectedItem = newItems[newItems.length - 1];
      setCurrentImageUrl(previousSelectedItem.picture_thumbnail_url);
      setCurrentImageId(previousSelectedItem.picture_id);
      if (previousSelectedItem.code) {
        setIsDelegated(false);
        setCurrentCode(previousSelectedItem.code);
      } else {
        setIsDelegated(true);
        setCurrentCode('');
      }
      setOrder({
        id: null,
        listing_id: listing.id,
        virtualServiceOrderItems: newItems,
        user_id: user.id,
        created_at: new Date(),
        shooting_id: listing.validated_shooting_id,
        service_offer_id: selectedService.id,
      });
    }
    setSelectedImages(newSelectedImages);
    setItems(newItems);
  };
  const buttons = (
    <Box>
      <HvButton className={styles.cancelButton} onClick={onBack}>
        {intl.formatMessage({ id: 'deliver.photos.back' })}
      </HvButton>
      <HvButton disabled={!virtualServiceOrder} onClick={onNext}>
        {intl.formatMessage({ id: 'deliver.photos.next' })}
      </HvButton>
    </Box>
  );
  const filteredPictures = listing.pictures.filter((pic: Picture) => {
    return !pic.show_watermark || (pic.show_watermark && pic.is_purchased);
  });
  return (
    <HvModal
      title={intl.formatMessage({ id: 'deliver.photos.virtualServiceModal.photoOptions' })}
      buttons={buttons}
      headerImage='virtualStagingBg'
      onClose={onClose}
      maxWidth='md'
    >
      <Box className={styles.content}>
        <Box className={styles.selectedImageContent}>
          <img src={currentImageUrl} alt='selected_photo' className={styles.selectedImage} />
        </Box>
        <Grid container spacing={1} className={styles.thumbnails}>
          {filteredPictures.map((pic: Picture, _index: number) => {
            if (selectedImages.includes(pic.id)) {
              return (
                <Grid item md={3} key={_index} className={styles.thumbnailContent}>
                  <CheckBoxIcon
                    color='primary'
                    className={styles.checkbox}
                    onClick={() => {
                      return unselect(pic);
                    }}
                  />
                  <LazyLoadImage
                    className={styles.image}
                    style={{
                      outline: pic.id === currentImageId ? 'solid 3px #3f51b5' : 'none',
                      outlineOffset: -2,
                      display: 'block',
                    }}
                    src={pic.thumbnailURL}
                    width={110}
                    height='auto'
                    alt='Thumbnail'
                    onClick={(_e: MouseEvent) => {
                      return onClickImage(pic);
                    }}
                  />
                </Grid>
              );
            }
            return (
              <Grid item md={3} key={_index} className={styles.selectedThumbnailContent}>
                <LazyLoadImage
                  className={styles.image}
                  src={pic.thumbnailURL}
                  width={110}
                  height='auto'
                  alt='Thumbnail'
                  onClick={(_e: MouseEvent) => {
                    return onClickImage(pic);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </HvModal>
  );
}
export default VirtualServiceOption;
